import { useEffect, useState } from 'react';
import { Coins } from 'lucide-react';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { ClaimDrawer } from './ClaimDrawer';

/**
 * LO QUE SE ESPERA COBRAR Y TODAVÍA NO LLEGÓ.
 *
 * Cuando el seguro aprueba un suplemento hay plata en camino, pero eso vivía solo en la cabeza de
 * quien lo cerró: `expected_checks` no tenía una sola fila de tipo `supplement` y 68 de 71
 * suplementos cerrados no esperaban nada. Si el cheque no llegaba, nadie se enteraba.
 *
 * La lista se ordena por ANTIGÜEDAD, no por monto: lo que hace falta perseguir es lo que lleva más
 * tiempo sin aparecer. Un cheque grande de ayer no necesita a nadie; uno chico de hace 40 días sí.
 */
type Espera = {
  id: string; claimId: string; claimNumber: string | null; homeowner: string | null;
  kind: string; label: string; amountCents: number; dias: number; source: string | null;
};

const usd = (c: number) => `$${(c / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

/** Color por antigüedad. Los cortes salen de la práctica: 30 días es el ciclo típico del seguro. */
const colorPorDias = (d: number) => (d >= 45 ? 'var(--red)' : d >= 30 ? 'var(--orange)' : 'var(--muted)');

export function EsperandoCheque({ minDias = 0 }: { minDias?: number }) {
  const { t } = useI18n();
  const [filas, setFilas] = useState<Espera[] | null>(null);
  const [sel, setSel] = useState<string | null>(null);
  const [soloViejas, setSoloViejas] = useState(false);

  useEffect(() => {
    let vivo = true;
    api.get<{ esperas: Espera[] }>(`/api/expected-checks/pending?minDias=${minDias}`)
      .then((r) => { if (vivo) setFilas(r.esperas); })
      .catch(() => { if (vivo) setFilas([]); });
    return () => { vivo = false; };
  }, [minDias]);

  const visibles = (filas ?? []).filter((f) => !soloViejas || f.dias >= 30);
  const total = visibles.reduce((s, f) => s + f.amountCents, 0);

  if (filas && filas.length === 0) return null; // sin nada esperando, no ocupa lugar

  return (
    <div className="card" style={{ display: 'grid', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <h2 className="card-title" style={{ margin: 0 }}><Coins size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('ec.title')}</h2>
        {filas && (
          <>
            <span className="badge gray" style={{ fontSize: 'var(--fs-2xs)' }}>{visibles.length}</span>
            <b style={{ fontSize: 'var(--fs-sm)', fontFamily: 'var(--font-mono)' }}>{usd(total)}</b>
          </>
        )}
        <span style={{ flex: 1 }} />
        <button className="btn ghost sm" style={{ fontSize: 'var(--fs-2xs)' }} onClick={() => setSoloViejas((v) => !v)}>
          {soloViejas ? t('ec.all') : t('ec.only30')}
        </button>
      </div>

      {!filas ? <div className="muted" style={{ fontSize: 'var(--fs-sm)' }}>{t('common.loading')}</div> : (
        <div style={{ display: 'grid', gap: 3, maxHeight: 320, overflowY: 'auto' }}>
          {visibles.map((f) => (
            <div key={f.id} onClick={() => setSel(f.claimId)}
              style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '5px 8px',
                borderRadius: 8, background: 'var(--surface-2)', cursor: 'pointer', fontSize: 'var(--fs-xs)' }}>
              <span style={{ minWidth: 52, fontWeight: 800, color: colorPorDias(f.dias) }}>{f.dias}d</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600 }}>
                  {f.homeowner || '—'} <span className="muted" style={{ fontWeight: 'var(--fw-body)', fontFamily: 'var(--font-mono)' }}>#{f.claimNumber || '—'}</span>
                </div>
                <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>
                  {f.label}
                  {/* Se distingue lo que anotó el sistema al cerrar un suplemento de lo que
                      alguien puso a mano: son dos niveles de confianza distintos. */}
                  {f.source === 'supplement_close' && ` · ${t('ec.fromSupplement')}`}
                </div>
              </div>
              <b style={{ fontFamily: 'var(--font-mono)' }}>{usd(f.amountCents)}</b>
            </div>
          ))}
        </div>
      )}

      {sel && <ClaimDrawer claimId={sel} onClose={() => setSel(null)} showChat />}
    </div>
  );
}
